/**
 * Component: SearchResultCard
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R09, FRONTEND-WEB-V1-R10,
 *               SEARCH-SEMANTIC-V1-R03, SEARCH-HYBRID-V1-R05,
 *               SEARCH-SIMPLE-V1-R04, BROWSE-V1-R20, BROWSE-V1-R21
 *
 * Single shared search result card component used by all three search pages
 * (Semantic Search, Galen Search, Simple Search). Extracted from duplicated
 * inline components to ensure consistent rendering (Bug #3870).
 *
 * Consistent features across all search modes:
 * - COR/LOE colour borders (admin-configurable via branding)
 * - COR/LOE text labels when present
 * - ExplanatoryText when present
 * - Captions/highlights fallback order: captions -> highlights -> truncated
 * - View Full Result link on all pages
 * - Expand/collapse for long content
 * - Bug #3863: AuthorDisplay XML stripped
 * - Bug #3864: Section vs Recommendation content fallback
 * - Bug #3876: Skip 200-char truncation for RecommendationText excerpts
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ExternalLink,
  ChevronDown,
  ChevronUp,
  FileText,
  BookOpen,
  User,
} from 'lucide-react';
import { useBranding } from '@/contexts';
import { trackUsageAction } from '@/lib/usage-tracking';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { stripHtml, getMetaString, formatPublishDate, containsRichBlock } from './search-result-utils';
import { SafeHtml } from '@/components/SafeHtml';
import { BrowseLink } from '@/features/browse';

/**
 * Common result item interface that works for all three search result types.
 * Fields unique to specific modes (captions, semantic_score) are optional.
 */
export interface SearchResultItem {
  document_id: string;
  title: string;
  content: string;
  score: number;
  semantic_score?: number | null;
  keyword_score?: number | null;
  captions?: string[];
  highlights: Record<string, string[]>;
  metadata: Record<string, unknown>;
  source_url: string | null;
}

export interface SearchResultCardProps {
  result: SearchResultItem;
  /** Display rank (1-based position on current page) */
  rank: number;
  /** Callback when View Full Result is clicked (opens detail panel) */
  onLinkOpen?: (documentId: string, rankingPosition: number, guidelineSectionId: number | null) => void;
  /** Optional query string for usage tracking context */
  query?: string;
  /** LOGGING-V1-R07: Search mode identifier for COUNTER referrer attribution */
  searchMode?: string;
}

export function SearchResultCard({
  result,
  rank,
  onLinkOpen,
  query,
  searchMode,
}: SearchResultCardProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  // Bug #5058: when collapsed, rich-block (table/figure) content is rendered in
  // full but height-clamped via CSS. This ref + flag detect whether the clamped
  // content actually overflows, so the "Show more" toggle is offered only when
  // there is genuinely hidden content (a one-row table that fits needs no toggle).
  const richRef = useRef<HTMLDivElement>(null);
  const [richOverflows, setRichOverflows] = useState(false);

  // FRONTEND-WEB-V1-R09: Access COR/LOE colour mappings from branding config
  const { branding } = useBranding();

  // Extract structured metadata fields
  const sectionTitle = getMetaString(result.metadata, 'GuidelineSectionName');
  const shortTitle = getMetaString(result.metadata, 'ShortTitle');
  const guidelineName = getMetaString(result.metadata, 'GuidelineName');
  const publishDateRaw = getMetaString(result.metadata, 'PublishDate');
  const publishDate = publishDateRaw ? formatPublishDate(publishDateRaw) : '';
  const guidelineUrl = getMetaString(result.metadata, 'ExternalGuidelineURL');
  const recommendationStrength = getMetaString(result.metadata, 'RecommendationStrength');
  const evidenceStrength = getMetaString(result.metadata, 'EvidenceStrength');
  // Bug #3878: Extract GuidelineSectionId for View Full Result detail panel
  const guidelineSectionIdRaw = result.metadata['GuidelineSectionId'];
  const guidelineSectionId = guidelineSectionIdRaw != null && guidelineSectionIdRaw !== 'null'
    ? Number(guidelineSectionIdRaw)
    : null;
  // Bug #3959: Extract GuidelineId for COUNTER resource identification (LOGGING-V1-R07)
  // Search index uses 'Guidelineid' (lowercase d, numeric) — read raw to avoid
  // stripHtml discarding the number, then fall back to 'GuidelineId' and document_id.
  const guidelineIdVal = result.metadata['Guidelineid'] ?? result.metadata['GuidelineId'];
  const guidelineId =
    typeof guidelineIdVal === 'number' ||
    (typeof guidelineIdVal === 'string' && guidelineIdVal !== 'null')
      ? String(guidelineIdVal)
      : result.document_id;
  // Bug #3863: Strip XML/HTML markup from AuthorDisplay to show clean author names
  const authorDisplay = getMetaString(result.metadata, 'AuthorDisplay');
  // Bug #3864: Determine result type from RecommendationId to select correct excerpt text.
  // Section results (RecommendationId is null) use SectionText from metadata;
  // Recommendation results (RecommendationId > 0) use result.content (RecommendationText).
  const recommendationId = result.metadata['RecommendationId'];
  const isRecommendation = recommendationId != null && recommendationId !== 'null' && Number(recommendationId) > 0;
  const sectionText = getMetaString(result.metadata, 'SectionText');
  const cleanContent = stripHtml(isRecommendation ? result.content : (sectionText || result.content));
  // Bug #3884 / #5058: Keep raw HTML for rendered contexts (expanded view, table
  // rendering). getMetaString() strips tags, so read SectionText raw here — using
  // the stripped value flattened table/figure markup before it reached SafeHtml.
  const rawSectionText =
    typeof result.metadata['SectionText'] === 'string' ? result.metadata['SectionText'] : '';
  const rawContent = isRecommendation ? result.content : (rawSectionText || result.content);
  // Bug #5058: Table/figure content must render as HTML (not flattened to text).
  // When present, the card renders the full raw HTML via SafeHtml instead of the
  // stripped, truncated excerpt — a table can't be truncated to 200 chars or
  // stripped without becoming an unreadable run-on blob. For sections it stays
  // collapsed (height-clamped) by default with a "Show more" toggle; only
  // Recommendations (which always show in full) render unclamped.
  const hasRichBlock = containsRichBlock(rawContent);
  const clampRichBlock = hasRichBlock && !isRecommendation && !isExpanded;
  const rawExplanatoryText = (result.metadata['ExplanatoryText'] as string) ?? '';

  // Bug #5058: measure whether the clamped rich-block content overflows its
  // collapsed height, so the "Show more" toggle is offered only when needed.
  // Measured on mount/content change while collapsed (the default); the result
  // is sticky across expand/collapse so the toggle does not vanish when expanded.
  useEffect(() => {
    const el = richRef.current;
    if (hasRichBlock && !isRecommendation && el) {
      setRichOverflows(el.scrollHeight > el.clientHeight + 4);
    }
  }, [hasRichBlock, isRecommendation, rawContent]);

  // Look up admin-configured COR/LOE colours
  const corColor = recommendationStrength
    ? branding.corColorMappings.find((m) => m.value === recommendationStrength)?.color ?? null
    : null;
  const loeColor = evidenceStrength
    ? branding.loeColorMappings.find((m) => m.value === evidenceStrength)?.color ?? null
    : null;

  /**
   * Handle link open: fires COUNTER tracking and opens detail panel.
   * FRONTEND-WEB-V1-R10: Track and display content when user opens a result.
   * Bug #3878: onLinkOpen now also passes GuidelineSectionId for detail panel.
   */
  const handleLinkOpen = useCallback(() => {
    // FRONTEND-WEB-V1-R10: Always fire COUNTER tracking
    // LOGGING-V1-R07: Include referrer_mode, section/publication IDs
    // Bug #3906: Add referrer_mode and section_id/publication_id
    // Bug #3911: Add guideline_name, external_guideline_url, section_name
    // Bug #3959: Include guideline_id for COUNTER resource identification
    void trackUsageAction('link_open', {
      document_id: result.document_id,
      ranking_position: rank,
      ...(query ? { query_text: query } : {}),
      ...(searchMode ? { referrer_mode: searchMode } : {}),
      ...(guidelineSectionId != null ? { section_id: guidelineSectionId } : {}),
      ...(guidelineName ? { guideline_name: guidelineName } : {}),
      ...(guidelineUrl ? { external_guideline_url: guidelineUrl } : {}),
      ...(sectionTitle ? { section_name: sectionTitle } : {}),
      ...(guidelineId ? { guideline_id: guidelineId } : {}),
    });
    // Bug #3878: Open detail panel via parent callback
    if (onLinkOpen) {
      onLinkOpen(result.document_id, rank, guidelineSectionId);
    }
  }, [result.document_id, rank, onLinkOpen, query, guidelineSectionId, searchMode, guidelineName, guidelineUrl, sectionTitle, guidelineId]);

  /**
   * Handle expand/collapse toggle with usage tracking.
   */
  const handleExpand = useCallback(() => {
    const expanding = !isExpanded;
    setIsExpanded(expanding);
    if (expanding) {
      // FRONTEND-WEB-V1-R10: Track result expansion
      void trackUsageAction('result_expand', {
        document_id: result.document_id,
        ranking_position: rank,
        ...(query ? { query_text: query } : {}),
      });
    }
  }, [isExpanded, result.document_id, rank, query]);

  /**
   * Render excerpt/highlight content (always visible).
   * Consistent fallback order: captions -> highlights -> truncated content.
   * Captions and highlights rendered as HTML to preserve <em> markup.
   */
  const renderExcerpt = (): React.ReactElement => {
    // Prefer semantic captions with highlight markup
    if (result.captions && result.captions.length > 0 && result.captions[0]) {
      return (
        <SafeHtml
          html={result.captions[0]}
          as="p"
          className="text-sm text-foreground [&_em]:font-semibold [&_em]:text-primary [&_em]:not-italic [&_em]:bg-yellow-100 [&_em]:dark:bg-yellow-900/40 [&_em]:px-0.5 [&_em]:rounded"
        />
      );
    }

    // Fallback to keyword highlights
    const contentHighlights = result.highlights?.['content'];
    if (contentHighlights && contentHighlights.length > 0 && contentHighlights[0]) {
      return (
        <SafeHtml
          html={contentHighlights[0]}
          as="p"
          className="text-sm text-foreground [&_em]:font-semibold [&_em]:text-primary [&_em]:not-italic [&_em]:bg-yellow-100 [&_em]:dark:bg-yellow-900/40 [&_em]:px-0.5 [&_em]:rounded"
        />
      );
    }

    // Bug #5058: Table/figure content renders as sanitised HTML so its structure
    // is preserved (matching View Full Result / Browse), never flattened to a
    // run-on text blob. The full HTML is always rendered (so it is never broken
    // mid-tag); when collapsed it is height-clamped with a fade, and the
    // "Show more" toggle reveals the rest. Recommendations render unclamped.
    if (hasRichBlock) {
      return (
        <div className="relative">
          <div
            ref={richRef}
            className={`text-sm text-foreground prose prose-sm max-w-none ${
              clampRichBlock ? 'max-h-40 overflow-hidden' : ''
            }`}
          >
            <SafeHtml html={rawContent} as="div" />
          </div>
          {clampRichBlock && richOverflows && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background to-transparent"
            />
          )}
        </div>
      );
    }

    // Final fallback: full text for Recommendations, first 200 chars for SectionText
    // Bug #3876: RecommendationText must not be truncated; only SectionText is truncated
    const truncatedContent =
      !isRecommendation && cleanContent.length > 200
        ? cleanContent.substring(0, 200) + '...'
        : cleanContent;
    return <p className="text-sm text-foreground">{truncatedContent}</p>;
  };

  // Bug #3876: Only SectionText has expandable content; Recommendations show in full.
  // Bug #5058: rich-block (table/figure) sections are collapsed (height-clamped) by
  // default and expand to full height — offer the toggle only when the clamped
  // content actually overflows. Plain sections keep the 200-char rule.
  const hasExpandableContent =
    !isRecommendation && (hasRichBlock ? richOverflows : cleanContent.length > 200);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="min-w-0 flex-1">
          {/* Header - Section Name (primary) */}
          <CardTitle className="text-base font-semibold">
            <span className="mr-2 text-muted-foreground">#{rank}</span>
            {sectionTitle || result.title}
          </CardTitle>
          {/* Line 2 - ShortTitle or GuidelineName */}
          {(shortTitle || guidelineName) && (
            <CardDescription className="mt-1 flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{shortTitle || guidelineName}</span>
            </CardDescription>
          )}
          {/* Bug #3863: AuthorDisplay - stripped of XML/HTML markup */}
          {authorDisplay && (
            <CardDescription className="mt-0.5 flex items-center gap-1.5 text-xs">
              <User className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{authorDisplay}</span>
            </CardDescription>
          )}
          {/* Published date */}
          {publishDate && (
            <CardDescription className="mt-0.5 text-xs">
              Published: {publishDate}
            </CardDescription>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* FRONTEND-WEB-V1-R09: Excerpt area with COR/LOE colour bands as left-edge decoration */}
        <div className="flex items-stretch">
          {/* Bug #3877: Colour bands as left-edge vertical strips on the excerpt area */}
          {(corColor || loeColor) && (
            <div className="flex shrink-0 gap-0.5" aria-hidden="true">
              {corColor && (
                <div
                  className="w-[0.4rem] shrink-0 rounded-l"
                  style={{ backgroundColor: corColor }}
                  role="img"
                  aria-label={`COR ${recommendationStrength} colour indicator`}
                />
              )}
              {loeColor && (
                <div
                  className={`w-[0.4rem] shrink-0 ${!corColor ? 'rounded-l' : ''}`}
                  style={{ backgroundColor: loeColor }}
                  role="img"
                  aria-label={`LOE ${evidenceStrength} colour indicator`}
                />
              )}
            </div>
          )}
          <div className={`min-w-0 flex-1 ${corColor || loeColor ? 'pl-2' : ''}`}>
            {/* FRONTEND-WEB-V1-R09: Collapsed excerpt view */}
            {renderExcerpt()}

            {/* ExplanatoryText (additional context) - consistent across all modes */}
            {/* Bug #3884: Render HTML with DOMPurify instead of stripping tags */}
            {rawExplanatoryText && (
              <SafeHtml
                html={rawExplanatoryText}
                as="div"
                className="mt-2 text-sm text-foreground italic"
              />
            )}

            {/* FRONTEND-WEB-V1-R09: Expanded full content */}
            {/* Bug #3884: Render HTML with DOMPurify to preserve formatting */}
            {/* Bug #5058: rich-block content is expanded in-place by renderExcerpt
                (the same node un-clamps), so skip the duplicate render here. */}
            {isExpanded && hasExpandableContent && !hasRichBlock && (
              <div className="mt-3 border-t pt-3">
                <SafeHtml
                  html={rawContent}
                  as="div"
                  className="text-sm text-foreground prose prose-sm max-w-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* FRONTEND-WEB-V1-R09: Expand/collapse toggle */}
        {hasExpandableContent && (
          <button
            type="button"
            onClick={handleExpand}
            className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse result details' : 'Expand result details'}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3 w-3" aria-hidden="true" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" aria-hidden="true" />
                Show more
              </>
            )}
          </button>
        )}

        {/* COR/LOE text labels - consistent across all modes */}
        {(recommendationStrength || evidenceStrength) && (
          <p className="mt-2 text-xs text-muted-foreground">
            {recommendationStrength && (
              <span>COR: {recommendationStrength}</span>
            )}
            {recommendationStrength && evidenceStrength && (
              <span className="mx-1.5">&middot;</span>
            )}
            {evidenceStrength && (
              <span>LOE: {evidenceStrength}</span>
            )}
          </p>
        )}
      </CardContent>
      <Separator />
      {/* Action links - consistent across all modes */}
      <CardFooter className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        {/* View Full Result - FRONTEND-WEB-V1-R10: COUNTER tracking */}
        <button
          type="button"
          onClick={handleLinkOpen}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          aria-label={`View full result for ${sectionTitle || result.title}`}
        >
          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
          View Full Result
        </button>
        {/* BROWSE-V1-R20: Cross-module Browse link. Rendered only
            when flags.browseLinks is true (BROWSE-V1-R18 flag);
            opens /browse/guidelines/{guidelineId}?sectionId={sectionId}
            in a new tab (BROWSE-V1-R21). R19 backend validator
            guarantees the /browse viewer is mounted whenever this
            flag is on. */}
        <BrowseLink
          guidelineId={guidelineId}
          sectionId={guidelineSectionId}
          searchMode={searchMode}
          documentId={result.document_id}
          rankingPosition={rank}
          guidelineName={guidelineName}
          sectionName={sectionTitle}
        />
        {/* View Full Guideline - external link */}
        {guidelineUrl && (
          <a
            href={guidelineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
            aria-label={`View full guideline for ${shortTitle || guidelineName || result.title}`}
            onClick={handleLinkOpen}
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            View Full Guideline
          </a>
        )}
      </CardFooter>
    </Card>
  );
}
